import { useState, useRef, useCallback } from 'react';
import { fetchApi } from '../../lib/api';

export default function NDASignature({ connection, companyName, documentUrl, ndaVersion, onComplete }) {
  const [signature, setSignature] = useState('');
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef(null);

  const isPdf = documentUrl && (documentUrl.endsWith('.pdf') || documentUrl.includes('supabase'));
  const isExternal = documentUrl && !isPdf;

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 50;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
      setScrolledToBottom(true);
    }
  }, []);

  const handleSubmit = async () => {
    if (!signature.trim() || !scrolledToBottom) return;
    setSubmitting(true);
    try {
      await fetchApi('/api/nda/sign', {
        method: 'POST',
        body: JSON.stringify({
          connection_id: connection.id,
          signature_text: signature.trim(),
          document_url: documentUrl,
          nda_version: ndaVersion || 1,
        }),
      });
      onComplete && onComplete();
    } catch (err) {
      alert('Failed to sign: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="glass p-7 max-w-3xl">
      <h2 className="font-heading text-xl text-vault-navy mb-1">Non-Disclosure Agreement</h2>
      <p className="text-sm text-gray-400 mb-6">Between you and {companyName}</p>

      {/* NDA Document Viewer */}
      <div className="mb-6">
        <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
          NDA Document — Read Before Signing
        </label>

        {isPdf ? (
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="border-2 border-vault-border rounded-btn overflow-hidden"
            style={{ height: 450 }}
          >
            <iframe
              src={documentUrl}
              title="NDA Document"
              className="w-full h-full"
              onLoad={() => {
                // For iframes, auto-mark as scrolled after a delay
                // since we can't detect scroll inside cross-origin iframes
                setTimeout(() => setScrolledToBottom(true), 5000);
              }}
            />
          </div>
        ) : isExternal ? (
          <div className="border-2 border-vault-border rounded-btn p-8 text-center">
            <p className="text-sm text-gray-500 mb-4">This NDA is hosted externally. Please read it fully before signing below.</p>
            <a
              href={documentUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-block px-6 py-2.5 rounded-btn text-sm font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
              onClick={() => setTimeout(() => setScrolledToBottom(true), 3000)}
            >
              Open NDA Document →
            </a>
            <p className="text-xs text-gray-400 mt-3">The signature field will unlock after you open the document.</p>
          </div>
        ) : (
          <div className="border-2 border-vault-border rounded-btn p-6 text-center text-sm text-gray-400">
            No NDA document has been provided by {companyName}.
          </div>
        )}

        {!scrolledToBottom && isPdf && (
          <p className="text-xs text-amber-600 mt-2 font-medium">
            Scroll to the bottom of the document to unlock the signature field.
          </p>
        )}
      </div>

      {/* E-SIGN Act Disclosure */}
      <div className="bg-amber-50 border border-amber-200 rounded-btn p-4 mb-6">
        <p className="text-sm text-amber-800 leading-relaxed">
          By typing your full legal name below, you are electronically signing this Non-Disclosure Agreement with {companyName}. This constitutes a legally binding electronic signature under the Electronic Signatures in Global and National Commerce Act (E-SIGN Act).
        </p>
      </div>

      {/* Signature Field */}
      <div className="mb-6">
        <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
          Type Your Full Legal Name
        </label>
        <input
          className="w-full px-4 py-3 rounded-input border-2 border-vault-border text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ fontFamily: "'EB Garamond', Georgia, serif", fontSize: 18 }}
          value={signature}
          onChange={e => setSignature(e.target.value)}
          placeholder="Your full legal name"
          disabled={!scrolledToBottom}
        />
        {!scrolledToBottom && (
          <p className="text-xs text-gray-400 mt-1">Read the NDA document above to unlock this field.</p>
        )}
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={!signature.trim() || !scrolledToBottom || submitting}
        className="w-full px-6 py-3 rounded-btn text-sm font-semibold text-white shadow-btn disabled:opacity-50 transition-all"
        style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
      >
        {submitting ? 'Signing...' : 'Sign & Submit'}
      </button>
    </div>
  );
}
